import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchAdminReturns,
  fetchAdminReturnDetail,
  submitReturnReview,
  markItemsReceived,
  processRefund,
  resetCurrentReturn,
} from '../store/slices/returnsAdminSlice';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/**
 * Hook for managing admin returns functionality
 * @returns {Object} Returns management methods and state
 */
const useAdminReturns = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('hooks');

  // Select state from Redux store
  const returns = useSelector((state) => state.adminReturns?.returns || []);
  const pagination = useSelector((state) => state.adminReturns?.pagination || null);
  const status = useSelector((state) => state.adminReturns?.status || 'idle');
  const error = useSelector((state) => state.adminReturns?.error || null);
  const currentReturn = useSelector((state) => state.adminReturns?.currentReturn || null);
  const actionStatus = useSelector((state) => state.adminReturns?.actionStatus || 'idle');
  const actionError = useSelector((state) => state.adminReturns?.actionError || null);

  // State for pagination and filtering, managed by the hook
  const [currentPageInternal, setCurrentPageInternal] = useState(1); // 1-based for API
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilterState] = useState(''); // Renamed setter for clarity
  const [searchTerm, setSearchTermState] = useState(''); // Renamed setter for clarity

  /**
   * Load returns data based on pagination and filter state
   */
  const loadReturns = useCallback(() => {
    // Ensure page and limit are valid numbers
    const page = isNaN(currentPageInternal) ? 1 : currentPageInternal;
    const limit = isNaN(rowsPerPage) ? 10 : rowsPerPage;

    console.log('Loading returns with filters:', {
      page,
      limit,
      status: statusFilter,
      search: searchTerm,
    });

    // Prepare query parameters
    const params = {
      page,
      limit,
    };

    // Only add status filter if it's not empty
    if (statusFilter) {
      params.status = statusFilter;
    }

    // Only add search term if it's not empty
    if (searchTerm) {
      params.search = searchTerm;
    }

    dispatch(fetchAdminReturns(params));
  }, [dispatch, currentPageInternal, rowsPerPage, statusFilter, searchTerm]);

  /**
   * Load returns when dependencies change
   */
  useEffect(() => {
    // Only load returns list on pages that need it
    // Don't load returns on detail page to avoid unnecessary API calls
    const isDetailPage =
      window.location.pathname.includes('/admin/returns/') &&
      !window.location.pathname.endsWith('/returns');

    if (!isDetailPage) {
      console.log('Loading returns list (not on detail page)');
      loadReturns();
    } else {
      console.log('Skipping loadReturns on detail page');
    }
  }, [loadReturns]); // loadReturns itself will change if its dependencies change

  // UI-facing pagination (0-indexed)
  const currentPageForUI = currentPageInternal - 1;

  const handlePageChangeForUI = (event, newPage) => {
    // newPage is 0-indexed, ensure it's a number
    const pageForAPI = (isNaN(newPage) ? 0 : newPage) + 1; // Convert to 1-indexed for API
    console.log(`Admin - Setting API page to ${pageForAPI} from UI page ${newPage}`);
    setCurrentPageInternal(pageForAPI);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPageInternal(1); // Reset to first page when rows per page changes
  };

  // Getter for totalReturns, derived from pagination object
  const totalReturns = pagination?.totalItems || 0;

  /**
   * Navigate to return detail page
   * @param {string} returnId - Return ID
   */
  const viewReturnDetail = (returnId) => {
    if (!returnId) {
      console.error('Cannot navigate to return detail: returnId is undefined');
      return;
    }

    // Clean up the ID just in case
    const cleanId = returnId.trim();

    console.log(`Navigating to return detail for ID: ${cleanId}`);
    navigate(`/admin/returns/${cleanId}`);
  };

  /**
   * Load single return detail
   * @param {string} returnId - Return ID to load
   */
  const loadReturnDetail = useCallback(
    (returnId) => {
      if (!returnId) {
        console.error('Cannot load return detail: returnId is undefined');
        return () => {};
      }

      // Remove any potential non-UUID characters (e.g., if extracted from URL)
      const cleanId = returnId.trim();

      console.log(`Loading return detail for ID: ${cleanId}`);
      dispatch(fetchAdminReturnDetail(cleanId));

      return () => {
        dispatch(resetCurrentReturn());
      };
    },
    [dispatch],
  );

  /**
   * Submit a review for a return request
   * @param {Object} reviewData - Review data
   * @param {string} reviewData.returnId - Return ID
   * @param {Array} reviewData.itemDecisions - Item decisions
   * @param {string} reviewData.globalAdminNotes - Admin notes
   */
  const reviewReturn = useCallback(
    (reviewData) => {
      dispatch(submitReturnReview(reviewData))
        .unwrap()
        .then(() => {
          toast.success(t('adminReturns.reviewSuccess'));
        })
        .catch((err) => {
          toast.error(err || t('adminReturns.reviewError'));
        });
    },
    [dispatch, t],
  );

  /**
   * Mark items as received
   * @param {Object} receiptData - Receipt data
   * @param {string} receiptData.returnId - Return ID
   * @param {Array} receiptData.itemReceipts - Item receipts
   * @param {string} receiptData.globalAdminNotes - Admin notes
   */
  const markAsReceived = useCallback(
    (receiptData) => {
      dispatch(markItemsReceived(receiptData))
        .unwrap()
        .then(() => {
          toast.success(t('adminReturns.receiptSuccess'));
        })
        .catch((err) => {
          toast.error(err || t('adminReturns.receiptError'));
        });
    },
    [dispatch, t],
  );

  /**
   * Process refund for a return
   * @param {Object} refundData - Refund data
   * @param {string} refundData.returnId - Return ID
   * @param {string} refundData.refundMethod - Refund method
   * @param {string} refundData.adminNotes - Admin notes
   */
  const processReturnRefund = useCallback(
    (refundData) => {
      dispatch(processRefund(refundData))
        .unwrap()
        .then(() => {
          toast.success(t('adminReturns.refundSuccess'));
        })
        .catch((err) => {
          toast.error(err || t('adminReturns.refundError'));
        });
    },
    [dispatch, t],
  );

  // Return methods and state
  return {
    // Data
    returns,
    currentReturn,
    totalReturns,

    // Status & Error
    status,
    error,
    actionStatus,
    actionError,

    // Pagination & Filter State and Setters for UI
    currentPage: currentPageForUI, // 0-indexed for UI
    setCurrentPage: handlePageChangeForUI, // Expects 0-indexed page from UI
    rowsPerPage,
    setRowsPerPage: handleRowsPerPageChange,
    statusFilter,
    setStatusFilter: setStatusFilterState, // Expose the direct state setter
    searchTerm,
    setSearchTerm: setSearchTermState, // Expose the direct state setter

    // Actions
    loadReturns,
    viewReturnDetail,
    loadReturnDetail,
    reviewReturn,
    markAsReceived,
    processReturnRefund,
  };
};

export default useAdminReturns;
